import axios from "axios";
import { ContactUsFormData } from "../../components/forms/contact/ContactUs.form";
import { IEventRequestPayload } from "../state/events/event";
import { IPaymentRequestPayload } from "../state/payments/payment";
import { IPromotionRequestPayload } from "../state/promotions/promotion";
import { ISubscription } from "../state/subscriptions/subscription";

export type IDisplayUser = {
  name: string;
  email: string;
  vendor: string;
  image: string;
  role: "admin" | "paid" | "new";
};

export const BASE_URL = `https://pmin-api.fly.dev`;
axios.defaults.withCredentials = false;
// =====================================
/**
 * AUTH - Login request for user
 */
export const getToken = async (credentials: string) => {
  return axios.get(`${BASE_URL}/auth/token?${credentials}`, {
    headers: {
      Accept: "*/*",
      "Content-Type": "application/json",
    },
    withCredentials: true,
  });
};

// USER RELATED =====================================

/**
 * Fetch User Details
 * @param accessToken string
 * @returns
 */
export const getUserDetails = async (token: string) => {
  return axios.get(`${BASE_URL}/auth/admin`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Bugfix
 * Fetch User Image
 * @param accessToken string
 * @returns
 */
export const getUserImageGoogle = async (token: string, url: string) => {
  return axios.get(`${url}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

// ASSETS ================================================================

/**
 *
 */
export const postBrochureFile = async (token: string, formData: FormData) => {
  return axios.post(`${BASE_URL}/assets/brochure`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Get all uploaded brochure files
 */
export const getAllBrochures = async (token: string) => {
  return axios.get(`${BASE_URL}/assets/brochures/`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 *
 */
export const postCertificateFile = async (
  token: string,
  formData: FormData
) => {
  return axios.post(`${BASE_URL}/assets/certificate/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Get all uploaded certificate files
 */
export const getAllCertificates = async (token: string) => {
  return axios.get(`${BASE_URL}/assets/certificates/`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

// EVENTS ================================================================

/**
 * Fetch event details
 * @returns Promise<IEvent[]>
 */
export const getEvents = async () => {
  return axios.get(`${BASE_URL}/events/`);
};

/**
 * Fetch event details for admin
 * @returns Promise<IEvent[]>
 */
export const getEventsAdmin = async (token: string) => {
  return axios.get(`${BASE_URL}/events/admin/all`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Post new event
 * @param {string} token
 * @param {IEvent} data
 * @returns Promise<string>
 */
export const postEvent = async (token: string, data: IEventRequestPayload) => {
  return axios.post(`${BASE_URL}/events/`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Update event by id
 * @param {string} token
 * * @param {string} id
 * @param {IEvent} data
 * @returns Promise<string>
 */
export const updateEventById = async (
  token: string,
  id: string,
  data: IEventRequestPayload
) => {
  return axios.put(`${BASE_URL}/events/${id}`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Delete event by id
 * @param {string} token
 * @param {string} id
 * @returns Promise<string>
 */
export const deleteEventById = async (token: string, id: string) => {
  return axios.delete(`${BASE_URL}/events/${id}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

// PAYMENTS ============================================================

/**
 * Fetch payment signature
 * /payments/signature/stripe
 */
export const getPaymentSignature = async (token: string) => {
  return axios.get(`${BASE_URL}/payments/signature/stripe`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Fetch payment details
 * @returns
 */
export const getAllPayments = async () => {
  return axios.get(`${BASE_URL}/payments/`);
};

/**
 * Post new payment record
 * @param {string} token
 * @param {IPaymentRequestPayload} data
 * @returns Promise<string>
 */
export const postPaymentAdmin = async (
  token: string,
  data: IPaymentRequestPayload
) => {
  return axios.post(`${BASE_URL}/payments/`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Update payment by id
 * @param {string} token
 * * @param {string} id
 * @param {IPaymentRequestPayload} data
 * @returns Promise<string>
 */
export const updatePaymentByIdAdmin = async (
  token: string,
  id: string,
  data: IPaymentRequestPayload
) => {
  return axios.put(`${BASE_URL}/payments/${id}`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Delete payment by id
 * @param {string} token
 * @param {string} id
 * @returns Promise<string>
 */
export const deletePaymentByIdAdmin = async (token: string, id: string) => {
  return axios.delete(`${BASE_URL}/payments/${id}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Create Stripe payment intent
 * @param vendor string
 * @param amount string
 * @param description string
 * @returns Promise<any>
 */
export const createStripePaymentIntent = async (
  amount: number,
  description: string
) => {
  return axios.post(
    `${BASE_URL}/payments/create-payment-intent`,
    {
      vendor: "stripe",
      amount: amount * 100,
      description,
    },
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
};

// PROMOTIONS ============================================================

/**
 * Fetch promotion record details
 * @returns
 */
export const getAllPromotionsAdmin = async (token: string) => {
  return axios.get(`${BASE_URL}/promotions/`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Post new promotion record
 * @param {string} token
 * @param {IPromotionRequestPayload} data
 * @returns Promise<string>
 */
export const postPromotionAdmin = async (
  token: string,
  data: IPromotionRequestPayload
) => {
  return axios.post(`${BASE_URL}/promotions/`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Update promotion by id
 * @param {string} token
 * * @param {string} id
 * @param {IPromotionRequestPayload} data
 * @returns Promise<string>
 */
export const updatePromotionByIdAdmin = async (
  token: string,
  id: string,
  data: IPromotionRequestPayload
) => {
  return axios.put(`${BASE_URL}/promotions/${id}`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Delete promotion by id
 * @param {string} token
 * @param {string} id
 * @returns Promise<string>
 */
export const deletePromotionByIdAdmin = async (token: string, id: string) => {
  return axios.delete(`${BASE_URL}/promotions/${id}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

// SUBSCRIPTIONS ============================================================

/**
 * Fetch All subscription record. Admin access
 * @param {string} token
 * @returns Promise<string>
 */
export const getAllSubscriptions = async (token: string) => {
  return axios.get(`${BASE_URL}/subscriptions/`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Query subscription record by text. Admin access
 * @param {string} token
 * @returns Promise<string>
 */
export const searchSubscription = async (token: string, text: string) => {
  return axios.get(`${BASE_URL}/subscriptions/text/search?text=${text}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Get subscription details by id. Admin access
 * @param {string} token
 * @returns Promise<string>
 */
export const getSubscriptionsDetailsById = async (
  token: string,
  id: string
) => {
  return axios.get(`${BASE_URL}/subscriptions/${id}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Post new subscription record
 * @param {string} token
 * @param {ISubscription} data
 * @returns Promise<string>
 */
export const postNewSubscription = async (
  token: string,
  data: ISubscription
) => {
  return axios.post(`${BASE_URL}/subscriptions/`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Update subscription record status by id
 * @param {string} token
 * @param {string} id
 * @param {string} status
 * @returns Promise<string>
 */
export const updateSubscriptionStatusById = async (
  token: string,
  id: string,
  status: string
) => {
  return axios.put(
    `${BASE_URL}/subscriptions/status/${id}`,
    {
      status: status,
    },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );
};

/**
 * Retrieve subject details by ID
 * @param {string} token
 * @param {string} id
 * @returns Promise<any>
 */
export const retrieveSubscriptionDetails = async (
  token: string,
  id: string
) => {
  return axios.get(`${BASE_URL}/subscriptions/${id}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

// EMAILS ===================================================================

/**
 * Send "Contact Us" Email to Sales Admin
 * @param {string} token
 * @param {ContactUsFormData} data
 * @returns Promise<string>
 */
export const postContactUsSalesAdminEmail = async (
  token: string,
  data: ContactUsFormData
) => {
  return axios.post(`${BASE_URL}/email/rmu`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Send "Contact Us" Receipt Email to Customer
 * @param {string} token
 * @returns Promise<string>
 */
export const postContactUsReceiptEmail = async (token: string) => {
  return axios.get(`${BASE_URL}/email/contactus`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Send "Contact Us" Receipt Email to Customer
 * @param {string} token
 * @returns Promise<string>
 */
export const postSubscriptionEmail = async (token: string, data: any) => {
  return axios.post(`${BASE_URL}/email/payment`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Send "Offline" Registration email to customer
 * @param {string} token
 * @returns Promise<string>
 */
export const postOfflineRegistrationEmail = async (
  token: string,
  data: any
) => {
  return axios.post(`${BASE_URL}/email/offline_rmu`, data, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });
};
